import { createSelector } from '@reduxjs/toolkit';
import { JURISDICTION_VIEW } from '@rsos/capstone/src/constants/capabilities';
import {
  selectCapstoneAudioSettingsForFeature,
  selectUserAudioSettingsForFeature,
} from '@rsos/sinatra/src/selectors/selectAudioSettings';
import { User } from '@rsos/utils/useTalkJS';
import { playTone } from './audioHelpers';
import {
  AudioNotificationsGlobalState,
  NOTIFICATION_COMPLETE,
} from './audioNotificationTypes';
import { selectHasUnreadTextMessage } from './smsHelpers';

export const selectIsUserParticipant = createSelector(
  [
    (_state, callerID) => callerID,
    state => state.rcs.conversations,
    state => state.textFrom911.conversations,
    state => state.conversations,
    state => state.sinatra.user.profile.id,
  ],
  (
    callerID,
    rcsConversations,
    textFrom911Conversations,
    conversations,
    userID,
  ) => {
    const checkParticipants = (participants: User[]) =>
      participants?.find(
        participant => String(participant.id) === String(userID),
      ) !== undefined;

    // Check RCS slice
    if (rcsConversations[callerID]?.participants) {
      if (checkParticipants(rcsConversations[callerID].participants)) {
        return true;
      }
    }

    // Check TextFrom911 slice
    const textFrom911Conversation = textFrom911Conversations?.[callerID];
    if (textFrom911Conversation?.participants) {
      if (checkParticipants(textFrom911Conversation.participants)) {
        return true;
      }
    }

    // Check Conversations slice
    const conversation = conversations?.[callerID];
    if (conversation?.participants) {
      if (checkParticipants(conversation.participants)) {
        return true;
      }
    }

    // Return false if no matching participant is found
    return false;
  },
);

export async function playUnreadMessageNotificationIfNecessary(
  state: AudioNotificationsGlobalState,
  id: string,
  isInitial = false,
) {
  const hasJurisdictionView =
    state.psaps.currentPSAP.activeCapabilities[JURISDICTION_VIEW];
  if (!hasJurisdictionView) return NOTIFICATION_COMPLETE();

  const orgSettings = selectCapstoneAudioSettingsForFeature('sms')(state);
  const orgSettingsFollowUp = orgSettings.follow_up;
  const userSettings = selectUserAudioSettingsForFeature('sms')(state);

  const [callerID, messageID] = id.split('.');
  const isParticipant = selectIsUserParticipant(state, callerID);

  // Check if audio is suppressed for this callerID
  if (state.audioNotifications.suppressedAudio === callerID) {
    return NOTIFICATION_COMPLETE();
  }

  // If we have an id specific to this one message, we can mark the notification
  // as complete. If we only have the callerID, we have to assume each
  // notification is for a new message, whether that's true or not:
  const status = messageID ? NOTIFICATION_COMPLETE() : undefined;

  const unread = selectHasUnreadTextMessage(state, callerID);
  if (!unread) return status;

  if (isInitial) {
    if (orgSettings.sound_on) {
      await playTone(orgSettings.tone, userSettings.volume);
    }
    return status;
  }

  // Determine the appropriate settings based on participant status
  const followup = isParticipant
    ? orgSettingsFollowUp.active_participants.sound_on
      ? orgSettingsFollowUp.active_participants
      : orgSettingsFollowUp.everyone_else
    : orgSettingsFollowUp.everyone_else;

  // Play tone if the determined settings have sound enabled
  if (followup.sound_on) {
    await playTone(followup.tone, userSettings.volume);
    return status;
  }

  // If no settings have sound enabled, mark it notified
  return NOTIFICATION_COMPLETE();
}

export async function playUnreadAlertMessageNotificationIfNecessary(
  state: AudioNotificationsGlobalState,
  messageID: string,
) {
  const orgSettings = selectCapstoneAudioSettingsForFeature('alerts')(state);
  const userSettings = selectUserAudioSettingsForFeature('alerts')(state);

  // If the alert conversation is currently open, mark as notified
  const alertID = messageID.split('.')[0];
  if (state.incidents.selectedAlertID == alertID)
    return NOTIFICATION_COMPLETE();

  // If the sound is turned off, mark it notified
  if (!orgSettings.sound_on) return NOTIFICATION_COMPLETE();

  // Alert conversations have a hard-coded sound
  await playTone('alertUpdateSound', userSettings.volume);

  return NOTIFICATION_COMPLETE();
}
